import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FiPhone } from "react-icons/fi";
import { db } from "./firebase";
import "./TrackOrder.css";

const createSVGIcon = (svg) => {
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

const pickupSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#16a34a" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3" fill="white"></circle></svg>`;

const dropoffSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><path d="M12 7v6m-3-3h6" stroke="white" stroke-width="2" stroke-linecap="round"></path></svg>`;

const deliveredSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`;

const truckSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#2563eb" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 19H4v-5h10m0 0V7a2 2 0 0 1 2-2h3l4 5v7h-9Z"></path><circle cx="7" cy="19" r="2"></circle><circle cx="17" cy="19" r="2"></circle></svg>`;

const statusClasses = {
  completed: "status-completed",
  accepted: "status-in-transit",
  "in transit": "status-in-transit",
  ongoing: "status-in-transit",
  pending: "status-waiting",
  requested: "status-waiting",
};

function normalizeStatus(status) {
  return String(status || "").trim().toLowerCase();
}

function getStatusClass(status) {
  return statusClasses[normalizeStatus(status)] || "status-default";
}

function humanizeStatus(status) {
  if (!status) return "Unknown";
  const value = String(status).trim();
  if (normalizeStatus(value) === "completed") return "Delivered";
  if (normalizeStatus(value) === "accepted") return "Accepted";
  if (normalizeStatus(value) === "in transit") return "In Transit";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function TrackOrder() {
  const params = useParams();
  let orderId = String(params.orderId || "");
  try {
    orderId = decodeURIComponent(orderId);
  } catch (err) {
    // ignore invalid encoded characters
  }
  orderId = orderId.trim().replace(/[#?].*$/g, "");
  const [order, setOrder] = useState(null);
  const [driver, setDriver] = useState(null);
  const [driverLoc, setDriverLoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const driverMarkerRef = useRef(null);

  const hasAssignedDriver = !!order?.assignedDriver;

  useEffect(() => {
    if (!orderId) {
      setError("Invalid tracking link.");
      setLoading(false);
      return;
    }

    let locationUnsubscribe = null;
    let orderUnsubscribe = null;

    const orderRef = doc(db, "orders", orderId);

    getDoc(orderRef)
      .then((snap) => {
        if (!snap.exists()) {
          setError("Order not found.");
          setLoading(false);
          return;
        }

        let data = snap.data();

        const processOrder = (orderData) => {
          setOrder(orderData);
          setLoading(false);

          if (orderData.assignedDriver) {
            const driverRef = doc(db, "users", orderData.assignedDriver);
            getDoc(driverRef).then((driverSnap) => {
              if (driverSnap.exists()) {
                setDriver(driverSnap.data());
              }
            });

            const locationRef = doc(db, "userBackgroundUpdates", orderData.assignedDriver);
            locationUnsubscribe = onSnapshot(
              locationRef,
              (snapLocation) => {
                if (snapLocation.exists()) {
                  setDriverLoc(snapLocation.data());
                }
              },
              (err) => {
                console.warn("Driver location listener error", err);
              }
            );
          }
        };

        // If supplier_truck_request_id exists, fetch from SuppliersTruckRequests
        if (data.supplier_truck_request_id) {
          const supplierReqRef = doc(db, "SuppliersTruckRequests", data.supplier_truck_request_id);
          getDoc(supplierReqRef)
            .then((supplierSnap) => {
              if (supplierSnap.exists()) {
                const supplierData = supplierSnap.data();
                // Map supplier location format to order location format
                const mapLocation = (loc) => {
                  if (!loc) return null;
                  return {
                    lat: loc.latitude,
                    lng: loc.longitude,
                    formattedAddress: loc.formattedAddress,
                  };
                };
                data = {
                  ...data,
                  pickup: mapLocation(supplierData.pickup) || data.pickup,
                  dropoff: mapLocation(supplierData.dropoff) || data.dropoff,
                };
              }
              processOrder(data);
            })
            .catch(() => {
              // If supplier fetch fails, continue with order data
              processOrder(data);
            });
        } else {
          processOrder(data);
        }

        orderUnsubscribe = onSnapshot(
          orderRef,
          (snapOrder) => {
            if (!snapOrder.exists()) return;
            let latestOrder = snapOrder.data();

            // Fetch supplier data for real-time updates too
            if (latestOrder.supplier_truck_request_id) {
              const supplierReqRef = doc(db, "SuppliersTruckRequests", latestOrder.supplier_truck_request_id);
              getDoc(supplierReqRef)
                .then((supplierSnap) => {
                  if (supplierSnap.exists()) {
                    const supplierData = supplierSnap.data();
                    const mapLocation = (loc) => {
                      if (!loc) return null;
                      return {
                        lat: loc.latitude,
                        lng: loc.longitude,
                        formattedAddress: loc.formattedAddress,
                      };
                    };
                    latestOrder = {
                      ...latestOrder,
                      pickup: mapLocation(supplierData.pickup) || latestOrder.pickup,
                      dropoff: mapLocation(supplierData.dropoff) || latestOrder.dropoff,
                    };
                  }
                  setOrder(latestOrder);
                  if (normalizeStatus(latestOrder.status) === "completed" && locationUnsubscribe) {
                    locationUnsubscribe();
                    locationUnsubscribe = null;
                  }
                })
                .catch(() => {
                  setOrder(latestOrder);
                  if (normalizeStatus(latestOrder.status) === "completed" && locationUnsubscribe) {
                    locationUnsubscribe();
                    locationUnsubscribe = null;
                  }
                });
            } else {
              setOrder(latestOrder);
              if (normalizeStatus(latestOrder.status) === "completed" && locationUnsubscribe) {
                locationUnsubscribe();
                locationUnsubscribe = null;
              }
            }
          },
          (err) => {
            console.warn("Order listener error", err);
          }
        );
      })
      .catch((err) => {
        console.error(err);
        setError("Unable to load the tracking data.");
        setLoading(false);
      });

    return () => {
      if (locationUnsubscribe) locationUnsubscribe();
      if (orderUnsubscribe) orderUnsubscribe();
    };
  }, [orderId]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        zoomControl: true,
        attributionControl: false,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;
    const pickupCoords = order?.pickup?.lat && order?.pickup?.lng ? [order.pickup.lat, order.pickup.lng] : null;
    const dropoffCoords = order?.dropoff?.lat && order?.dropoff?.lng ? [order.dropoff.lat, order.dropoff.lng] : null;
    const defaultCenter = [-1.2921, 36.8219];
    const isDelivered = normalizeStatus(order?.status) === "completed";

    const pickupIcon = L.icon({
      iconUrl: createSVGIcon(pickupSVG),
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    });
    const dropoffIcon = L.icon({
      iconUrl: createSVGIcon(dropoffSVG),
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    });
    const deliveredIcon = L.icon({
      iconUrl: createSVGIcon(deliveredSVG),
      iconSize: [44, 44],
      iconAnchor: [22, 44],
      popupAnchor: [0, -44],
    });
    const truckIcon = L.icon({
      iconUrl: createSVGIcon(truckSVG),
      iconSize: [50, 50],
      iconAnchor: [25, 25],
      popupAnchor: [0, -25],
    });

    if (pickupCoords && dropoffCoords) {
      map.fitBounds([pickupCoords, dropoffCoords], { padding: [60, 60] });
    } else if (pickupCoords) {
      map.setView(pickupCoords, 13);
    } else if (dropoffCoords) {
      map.setView(dropoffCoords, 13);
    } else {
      map.setView(defaultCenter, 7);
    }

    if (pickupCoords) {
      if (!pickupMarkerRef.current) {
        pickupMarkerRef.current = L.marker(pickupCoords, { icon: pickupIcon, interactive: false }).addTo(map);
      } else {
        pickupMarkerRef.current.setLatLng(pickupCoords);
      }
    }

    if (dropoffCoords) {
      const markerIcon = isDelivered ? deliveredIcon : dropoffIcon;
      if (!dropoffMarkerRef.current) {
        dropoffMarkerRef.current = L.marker(dropoffCoords, { icon: markerIcon, interactive: false }).addTo(map);
      } else {
        dropoffMarkerRef.current.setIcon(markerIcon);
        dropoffMarkerRef.current.setLatLng(dropoffCoords);
      }
    }

    if (!isDelivered && driverLoc?.latitude && driverLoc?.longitude) {
      const driverPosition = [driverLoc.latitude, driverLoc.longitude];
      if (!driverMarkerRef.current) {
        driverMarkerRef.current = L.marker(driverPosition, { icon: truckIcon, interactive: false }).addTo(map);
      } else {
        driverMarkerRef.current.setLatLng(driverPosition);
      }
    } else if (isDelivered && driverMarkerRef.current) {
      map.removeLayer(driverMarkerRef.current);
      driverMarkerRef.current = null;
    }
  }, [order, driverLoc]);

  const renderBannerText = () => {
    if (error) return "Tracking unavailable";
    if (loading) return "Loading order...";
    if (!order) return "Order not found";
    return humanizeStatus(order.status);
  };

  const renderDetailRow = (label, value) => (
    <div className="track-detail-row">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );

  return (
    <div className="track-page-shell">
      <section className={`track-status-banner ${getStatusClass(order?.status)}`}>
        <div>
          <span className="track-badge">Status</span>
          <h1>{renderBannerText()}</h1>
        </div>
      </section>

      <main className="track-main-grid">
        <section className="track-map-panel">
          <div className="track-map-wrapper">
            <div className="track-map" ref={mapRef} />
            <div className="track-map-overlay">
              {error ? (
                <div>
                  <h2>Tracking unavailable</h2>
                  <p>{error}</p>
                </div>
              ) : loading ? (
                <div>
                  <h2>Loading order details…</h2>
                </div>
              ) : !hasAssignedDriver ? (
                <div>
                  <h2>Waiting for a driver</h2>
                  <p>The order has been placed, but a transporter is not assigned yet.</p>
                </div>
              ) : !order?.pickup?.lat || !order?.pickup?.lng || !order?.dropoff?.lat || !order?.dropoff?.lng ? (
                <div>
                  <h2>Location data unavailable</h2>
                  <p>Pickup and dropoff coordinates are required for live tracking.</p>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="track-details-panel">
          <div className="track-card">
            <div className="track-card-header">
              <div>
                <span className="track-card-label">Order</span>
                <h2>#{orderId}</h2>
                {order?.invoice_id && (
                  <p className="track-subtext">Invoice ID: {order.invoice_id}</p>
                )}
              </div>
              <div className="track-card-status">{humanizeStatus(order?.status)}</div>
            </div>

            <div className="track-details">
              {renderDetailRow("Document ID", orderId)}
              {order?.invoice_id && renderDetailRow("Invoice ID", `#${order.invoice_id}`)}
              {renderDetailRow("Goods", order?.goods)}
              {renderDetailRow("Pickup", order?.pickup?.formattedAddress)}
              {renderDetailRow("Dropoff", order?.dropoff?.formattedAddress)}
              {renderDetailRow("Vehicle", driver ? `${driver.vehicleNumber || "N/A"} · ${driver.vehicleColor || "N/A"} · ${driver.vehicleMake || "N/A"}` : "Not assigned")}
            </div>
          </div>

          <div className="track-card track-driver-card">
            <div className="track-card-header">
              <div>
                <span className="track-card-label">Driver</span>
                <h2>{driver?.fullName || "Driver info pending"}</h2>
              </div>
              {driver?.phone && (
                <a className="track-call-button" href={`tel:${driver.phone}`}>
                  <FiPhone size={18} style={{ marginRight: "6px" }} />
                  Call Driver
                </a>
              )}
            </div>

            <div className="track-details">
              {renderDetailRow("Phone", driver?.phone)}
              {renderDetailRow("Vehicle", driver ? `${driver.vehicleNumber || "N/A"} · ${driver.vehicleColor || "N/A"} · ${driver.vehicleMake || "N/A"}` : "—")}
              {renderDetailRow("Current", driverLoc ? `${driverLoc.latitude.toFixed(5)}, ${driverLoc.longitude.toFixed(5)}` : "Waiting for location")}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default TrackOrder;
